import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useTheme, useMediaQuery, makeStyles } from "@material-ui/core";
import { Button, Grid, CircularProgress, Tooltip } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { emptyObject } from "constants/defaultValues";
import { isDirty } from "@totalsoft/change-tracking";
import { get, set } from "@totalsoft/react-state-lens";
import { Payment, Check, Tune } from "@material-ui/icons";
import productPageStyle from "features/products/styles";
import useUserData from "hooks/useUserData";

const useStyles = makeStyles(productPageStyle);

function EditProductActions(props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const styleProps = useMemo(() => ({ fullScreen }), [fullScreen]);
  const classes = useStyles(styleProps);

  const { quote, productDirtyInfo, onSave, onComputeSchedule, showAdvancedProductEditAction } = props;
  const { openEditProductLens, settingsLens } = props;

  const { productLens } = props;
  const product = productLens |> get || emptyObject;

  const loading = props.loading || !quote || !product;
  const saving = props.saving;
  const { allowSelectionInsuranceConvention } = useUserData();

  const handleOpenBasicProductEdit = useCallback(() => {
    (openEditProductLens |> set)("BASIC");
  }, [openEditProductLens]);

  const handleOpenAdvancedProductEdit = useCallback(() => {
    (openEditProductLens |> set)("ADVANCED");
  }, [openEditProductLens]);

  const handleComputeSchedule = useCallback(() => {
    onComputeSchedule();
  }, [onComputeSchedule]);

  const handleOpenSchedule = useCallback(() => {
    (settingsLens.showProductSchedule |> set)(true);
  }, [settingsLens.showProductSchedule]);

  const handleOpenInsuranceDetails = useCallback(() => {
    (settingsLens.showInsurancePage |> set)(true);
  }, [settingsLens.showInsurancePage]);

  return (
    <Grid container spacing={1} direction="row" justifyContent="space-evenly">
      {showAdvancedProductEditAction ? (
        <Grid item>
          <Tooltip title={t("Product.EditAdvanced")}>
            <span>
              <Button
                id="advancedProductEdit"
                onClick={handleOpenAdvancedProductEdit}
                disabled={loading || saving || !product?.scheduleIsConfigurable}
                variant="contained"
                color="primary"
                className={classes.actionBtn}
                endIcon={<Tune />}
              >
                {t("Product.EditAdvanced")}
              </Button>
            </span>
          </Tooltip>
        </Grid>
      ) : (
        <Grid item>
          <Tooltip title={t("Product.EditBasic")}>
            <span>
              <Button
                id="advancedProductEdit"
                onClick={handleOpenBasicProductEdit}
                disabled={loading || saving}
                variant="contained"
                color="primary"
                className={classes.actionBtn}
                endIcon={<Tune />}
              >
                {t("Product.EditBasic")}
              </Button>
            </span>
          </Tooltip>
        </Grid>
      )}

      {product.addInsurance && allowSelectionInsuranceConvention && (
        <Grid item>
          <Tooltip title={t("Product.EditInsurance")}>
            <span>
              <Button
                id="editInsurance"
                disabled={loading || saving}
                onClick={handleOpenInsuranceDetails}
                variant="contained"
                color="primary"
                className={classes.actionBtn}
              >
                {t("Product.EditInsurance")}
              </Button>
            </span>
          </Tooltip>
        </Grid>
      )}

      <Grid item container xs={true} spacing={1} justifyContent="flex-end" alignItems="center">
        <Grid item>
          <Tooltip title={t("Product.Schedule")}>
            <span>
              <Button
                id="vwAdvancedProductSchedule"
                onClick={handleOpenSchedule}
                disabled={loading || saving || !(productLens.hasSchedule |> get)}
                variant="contained"
                color="primary"
                className={classes.actionBtn}
                endIcon={<Payment />}
              >
                <span style={{ padding: "5px 0" }}> {t("Product.Schedule")}</span>
              </Button>
            </span>
          </Tooltip>
        </Grid>
        {((productLens.hasSchedule |> get) || isDirty(productDirtyInfo)) && (
          <Grid item>
            <Tooltip title={t("General.SaveAndCalculate")}>
              <span>
                <Button
                  disabled={loading || saving || !isDirty(productDirtyInfo)}
                  onClick={onSave}
                  variant="contained"
                  color="primary"
                  endIcon={saving ? <CircularProgress size={15} color={"inherit"} /> : <Check />}
                  className={classes.actionBtn}
                >
                  {t("General.SaveAndCalculate")}
                </Button>
              </span>
            </Tooltip>
          </Grid>
        )}

        {!(productLens.hasSchedule |> get) && !isDirty(productDirtyInfo) && (
          <Grid item>
            <Tooltip title={t("Product.ComputeSchedule")}>
              <span>
                <Button
                  id="computeSchedule"
                  onClick={handleComputeSchedule}
                  disabled={loading || saving || isDirty(productDirtyInfo)}
                  variant="contained"
                  color="primary"
                  endIcon={saving ? <CircularProgress size={15} color={"inherit"} /> : <Check />}
                  className={classes.actionBtn}
                >
                  {t("Product.ComputeSchedule")}
                </Button>
              </span>
            </Tooltip>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}

EditProductActions.propTypes = {
  showAdvancedProductEditAction: PropTypes.bool.isRequired,
  openEditProductLens: PropTypes.object.isRequired,
  quote: PropTypes.object,
  productLens: PropTypes.object.isRequired,
  productDirtyInfo: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  onComputeSchedule: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  settingsLens: PropTypes.object.isRequired,
};

export default EditProductActions;
